import api from '@api';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import CreateGameModal from "./components/CreateGameModal";
import CreateByeModal from "./components/CreateByeModal";
import CreateWeekOffModal from "./components/CreateWeekOffModal";
import UpdateScoreModal from "./components/UpdateScoreModal";
import GameScheduleTable from "./components/GameScheduleTable";
import SavedLocationModals from "./components/SavedLocationModals";
import './LeagueGamesAuth.css';

function LeagueGamesAuth({ leagueId, seasonId, leagueType, teams: externalTeams }) {
  const [games, setGames] = useState([]);
  const uniqueDates = [...new Set(games.map(g => g.date))].sort();

  const [teams, setTeams] = useState([]);
  const [leaguePlayers, setLeaguePlayers] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGames, setSelectedGames] = useState([]);
  const [message, setMessage] = useState('');

  const [isScoreModalOpen, setIsScoreModalOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);

  const [dateFilters, setDateFilters] = useState([]);
  const [showDateFilterDropdown, setShowDateFilterDropdown] = useState(false);
  const [scoreMismatches, setScoreMismatches] = useState({});

  const [isByeModalOpen, setIsByeModalOpen] = useState(false);
  const [byeForm, setByeForm] = useState({ date: '', team: '' });
  const [byes, setByes] = useState([]);
  const [selectedByes, setSelectedByes] = useState([]);

  const [weekOffs, setWeekOffs] = useState([]);
  const [selectedWeekOffs, setSelectedWeekOffs] = useState([]);
  const [showWeekOffModal, setShowWeekOffModal] = useState(false);
  const [weekOffForm, setWeekOffForm] = useState({ date: '', reason: '' });

  const [savedLocations, setSavedLocations] = useState([]);
  const [showCreateLocationModal, setShowCreateLocationModal] = useState(false);
  const [showEditLocationsModal, setShowEditLocationsModal] = useState(false);
  const [newSavedLocationForm, setNewSavedLocationForm] = useState({
    name: '',
    location_url: '',
  });
  const [editingSavedLocations, setEditingSavedLocations] = useState([]);

  const navigate = useNavigate();

  const selectedItemCount =
    selectedGames.length + selectedByes.length + selectedWeekOffs.length;

  const toggleDateFilter = (date) => {
    setDateFilters((prev) => {
      const updated = prev.includes(date)
        ? prev.filter((d) => d !== date)
        : [...prev, date];

      sessionStorage.setItem("gameDateFilters", JSON.stringify(updated));
      return updated;
    });
  };

  const clearDateFilters = () => {
    setDateFilters([]);
    sessionStorage.removeItem("gameDateFilters");
  };

  const fetchData = async () => {
    try {
      const res = await api.get(`/api/seasonsv2/${seasonId}/leagues/${leagueId}/games`, {
        withCredentials: true
      });

      const fetchedGames = res.data.games || [];

      if (leagueType === "individual") {
        const playersRes = await api.get(
          `/api/seasonsv2/${seasonId}/leagues/${leagueId}/players`,
          { withCredentials: true }
        );
        setLeaguePlayers(playersRes.data.players || []);
      }

      const fetchedTeams = externalTeams && externalTeams.length
        ? externalTeams
        : (res.data.teams || []);

      setGames(fetchedGames);
      setTeams(fetchedTeams);

      const byeRes = await api.get(`/api/bye-dates/season/${seasonId}/league/${leagueId}`, {
        withCredentials: true
      });

      setByes(byeRes.data.byes || []);

      const offRes = await api.get(`/api/week-offs/season/${seasonId}/league/${leagueId}`, {
        withCredentials: true,
      });

      setWeekOffs(offRes.data || []);

      const savedLocationsRes = await api.get(`/api/saved-locations`, {
        withCredentials: true,
      });

      const saved = savedLocationsRes.data || [];
      setSavedLocations(saved);
      setEditingSavedLocations(saved);
    } catch (error) {
      console.error("❌ Error fetching games or byes:", error);
    }
  };

  useEffect(() => {
    if (!seasonId || !leagueId) return;
    fetchData();
  }, [seasonId, leagueId]);

  useEffect(() => {
    setTeams(externalTeams || []);
  }, [externalTeams]);

  useEffect(() => {
    const savedFilters = sessionStorage.getItem("gameDateFilters");

    if (savedFilters) {
      try {
        setDateFilters(JSON.parse(savedFilters));
      } catch {
        setDateFilters([]);
      }
    }
  }, []);

  useEffect(() => {
    const fetchMismatches = async () => {
      try {
        const res = await api.get(
          `/api/seasonsv2/${seasonId}/leagues/${leagueId}/score-mismatches`,
          { withCredentials: true }
        );

        setScoreMismatches(res.data.mismatches || {});
      } catch (err) {
        console.error("Error fetching mismatches", err);
      }
    };

    if (leagueType === "individual") {
      setScoreMismatches({});
      return;
    }

    if (games.length) {
      fetchMismatches();
    }
  }, [games, leagueType, seasonId, leagueId]);

  const handleCreateGames = async (gamesToCreate) => {
    if (!gamesToCreate.length) {
      return alert("No games selected to create.");
    }

    const isIndividual = leagueType === "individual";

    try {
      for (const game of gamesToCreate) {
        const payload = {
          date: game.date,
          time: game.time,
          location: game.location,
          location_url: game.location_url,
        };

        if (isIndividual) {
          payload.home_player_id = Number(game.team1_id);
          payload.away_player_id = Number(game.team2_id);

          payload.match_format = game.match_format;

          if (game.match_format === "doubles") {
            payload.home_partner_player_id = Number(game.team1_partner_id);
            payload.away_partner_player_id = Number(game.team2_partner_id);
          }
        } else {
          payload.home_team_id = Number(game.team1_id);
          payload.away_team_id = Number(game.team2_id);
        }

        await api.post(
          `/api/seasonsv2/${seasonId}/leagues/${leagueId}/games`,
          payload,
          { withCredentials: true }
        );
      }

      await fetchData();

      setIsModalOpen(false);
      setMessage(`${gamesToCreate.length} game${gamesToCreate.length === 1 ? "" : "s"} created successfully.`);
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("❌ Error creating games:", error?.response?.data || error.message || error);
      alert("Failed to create one or more games. Check the console for more details.");
    }
  };

  const handleCreateWeekOff = async (e) => {
    e.preventDefault();

    try {
      await api.post(`/api/week-offs`, {
        season_id: Number(seasonId),
        league_id: Number(leagueId),
        date: weekOffForm.date,
        reason: weekOffForm.reason,
      }, { withCredentials: true });

      await fetchData();

      setWeekOffForm({ date: '', reason: '' });
      setShowWeekOffModal(false);
      setMessage("Week off created!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("❌ Failed to create week off:", err);
      alert("Failed to create week off.");
    }
  };

  const handleCreateSavedLocation = async (e) => {
    e.preventDefault();

    if (!newSavedLocationForm.name.trim()) {
      return alert('Location name is required.');
    }

    try {
      await api.post(
        `/api/saved-locations`,
        {
          name: newSavedLocationForm.name,
          location_url: newSavedLocationForm.location_url,
        },
        { withCredentials: true }
      );

      setNewSavedLocationForm({ name: '', location_url: '' });
      setShowCreateLocationModal(false);
      await fetchData();
    } catch (error) {
      console.error('❌ Error creating saved location:', error);
      alert('Failed to create saved location.');
    }
  };

  const handleEditSavedLocationField = (id, field, value) => {
    setEditingSavedLocations((prev) =>
      prev.map((loc) =>
        loc.id === id ? { ...loc, [field]: value } : loc
      )
    );
  };

  const handleSaveEditedLocations = async (e) => {
    e.preventDefault();

    try {
      await Promise.all(
        editingSavedLocations.map((loc) =>
          api.put(
            `/api/saved-locations/${loc.id}`,
            {
              name: loc.name,
              location_url: loc.location_url,
            },
            { withCredentials: true }
          )
        )
      );

      setShowEditLocationsModal(false);
      await fetchData();
    } catch (error) {
      console.error('❌ Error updating saved locations:', error);
      alert('Failed to update saved locations.');
    }
  };

  const handleDeleteSavedLocation = async (id) => {
    const confirmed = window.confirm('Delete this saved location?');
    if (!confirmed) return;

    try {
      await api.delete(`/api/saved-locations/${id}`, {
        withCredentials: true,
      });

      await fetchData();
    } catch (error) {
      console.error('❌ Error deleting saved location:', error);
      alert('Failed to delete saved location.');
    }
  };

  const handleCreateBye = async (byePayload) => {
    try {
      const isIndividual = leagueType === "individual";

      const date = byePayload.date;
      const participantIds = byePayload.participantIds || [];

      if (!date) {
        return alert("Date is required.");
      }

      if (!participantIds.length) {
        return alert(isIndividual ? "Select at least one player." : "Select at least one team.");
      }

      await Promise.all(
        participantIds.map((participantId) =>
          api.post(
            `/api/bye-dates`,
            {
              date,
              ...(isIndividual
                ? { player_id: participantId }
                : { team_id: participantId }),
            },
            { withCredentials: true }
          )
        )
      );

      setByeForm({ date: '', team: '' });
      setIsByeModalOpen(false);
      setMessage(`${participantIds.length} bye${participantIds.length === 1 ? "" : "s"} created!`);
      setTimeout(() => setMessage(""), 3000);

      await fetchData();
    } catch (err) {
      console.error("Error creating bye:", err);
      alert("Failed to create bye");
    }
  };

  const openScoreModal = (game) => {
    setSelectedGame(game);
    setScoreForm({
      score_team1: game.score_team1 || '',
      score_team2: game.score_team2 || ''
    });
    setIsScoreModalOpen(true);
  };

  const [scoreForm, setScoreForm] = useState({
    score_team1: '',
    score_team2: ''
  });

  const handleScoreChange = (e) => {
    const { name, value } = e.target;
    setScoreForm(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateScore = async (e) => {
    e.preventDefault();

    if (!selectedGame) return;

    try {
      const res = await api.put(
        `/api/seasonsv2/${seasonId}/leagues/${leagueId}/games/${selectedGame.id}`,
        {
          score_home: scoreForm.score_team1,
          score_away: scoreForm.score_team2,
        },
        { withCredentials: true }
      );

      setGames(prev =>
        prev.map(game => (game.id === selectedGame.id ? { ...game, ...res.data } : game))
      );

      setMessage("Score updated successfully");
      setTimeout(() => setMessage(""), 3000);
      setIsScoreModalOpen(false);
    } catch (error) {
      console.error("Error updating score:", error);
    }
  };

  const getTeamNameById = (id) => {
    const team = teams.find(
      (team) =>
        Number(team.seasons_leagues_teams_id) === Number(id)
    );

    return team
      ? team.name || team.team?.name || "Unknown Team"
      : "Unknown Team";
  };

  const getGameDate = (game) => {
    if (game.teams && game.date && !game.status) {
      return format(parseISO(game.date), 'MMMM d, yyyy');
    }

    if (game.game_dateTime) {
      return formatInTimeZone(new Date(game.game_dateTime), 'America/Los_Angeles', 'MMMM d, yyyy');
    }

    if (game.date && game.time) {
      return formatInTimeZone(new Date(`${game.date}T${game.time}`), 'America/Los_Angeles', 'MMMM d, yyyy');
    }

    if (game.date) {
      return formatInTimeZone(new Date(game.date), 'America/Los_Angeles', 'MMMM d, yyyy');
    }

    return 'TBD';
  };

  const getGameTime = (game) => {
    if (game.date && game.time) {
      return formatInTimeZone(new Date(`${game.date}T${game.time}`), 'America/Los_Angeles', 'h:mm a');
    }

    if (game.game_dateTime) {
      return formatInTimeZone(new Date(game.game_dateTime), 'America/Los_Angeles', 'h:mm a');
    }

    return 'TBD';
  };

  const handleDeleteSelected = async () => {
    if (!selectedGames.length && !selectedByes.length && !selectedWeekOffs.length) {
      return alert("Select games, byes, or week-offs to delete.");
    }

    if (!window.confirm("Are you sure you want to delete the selected items?")) return;

    try {
      await Promise.all([
        ...selectedGames.map(id =>
          api.delete(`/api/seasonsv2/${seasonId}/leagues/${leagueId}/games/${id}`, {
            withCredentials: true
          })
        ),
        ...selectedByes.map(id =>
          api.delete(`/api/bye-dates/${id}`, { withCredentials: true })
        ),
        ...selectedWeekOffs.map(id =>
          api.delete(`/api/week-offs/${id}`, { withCredentials: true })
        ),
      ]);

      setGames(prev => prev.filter(g => !selectedGames.includes(g.id)));
      setByes(prev => prev.filter(b => !selectedByes.includes(b.id)));
      setWeekOffs(prev => prev.filter(w => !selectedWeekOffs.includes(w.id)));

      setSelectedGames([]);
      setSelectedByes([]);
      setSelectedWeekOffs([]);

      setMessage("Items deleted successfully");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("❌ Error deleting items:", err);
      alert("Error occurred while deleting.");
    }
  };

  const weekOffEntries = weekOffs.map((off) => ({
    id: off.id,
    date: off.date,
    reason: off.reason,
    type: "week_off",
  }));

  const byeEntries = byes.map((bye) => ({
    ...bye,
    type: "bye",
  }));

  const displayRows = [...games, ...byeEntries, ...weekOffEntries]
    .filter(
      (entry) =>
        dateFilters.length === 0 ||
        dateFilters.includes(entry.date)
    )
    .sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.time || "00:00"}`);
      const dateB = new Date(`${b.date}T${b.time || "00:00"}`);
      return dateA - dateB;
    });

  return (
    <div className="gameInLeagueAuthPage-container">
      <div className="gameInLeagueAuthPage-headerCard">
        <div className="gameInLeagueAuthPage-headerText">
          <h2 className="gameInLeagueAuthPage-title">Games</h2>
          <p className="gameInLeagueAuthPage-subtitle">
            Create games, manage schedule dates, and update scores for this league season.
          </p>
        </div>

        <div className="gameInLeagueAuthPage-headerActions">
          <button
            type="button"
            className="gameInLeagueAuthPage-primaryBtn"
            onClick={() => setIsModalOpen(true)}
          >
            + Add Games
          </button>

          <button
            type="button"
            className="gameInLeagueAuthPage-secondaryBtn"
            onClick={() => setIsByeModalOpen(true)}
          >
            + Add Bye
          </button>

          <button
            type="button"
            className="gameInLeagueAuthPage-secondaryBtn"
            onClick={() => setShowWeekOffModal(true)}
          >
            + Week Off
          </button>
          <button
            type="button"
            className="gameInLeagueAuthPage-dangerBtn"
            onClick={handleDeleteSelected}
            disabled={selectedItemCount === 0}
          >
            Delete Selected
            {selectedItemCount > 0 && ` (${selectedItemCount})`}
          </button>
        </div>
      </div>

      <div className="gameInLeagueAuthPage-toolbarCard">
        <div className="gameInLeagueAuthPage-toolbarLeft">
          <div className="gameInLeagueAuthPage-filterWrap">
            <button
              type="button"
              className="gameInLeagueAuthPage-filterBtn"
              onClick={() => setShowDateFilterDropdown(prev => !prev)}
            >
              Filter Dates
              {dateFilters.length > 0 && (
                <span className="gameInLeagueAuthPage-filterCount">
                  {dateFilters.length}
                </span>
              )}
            </button>

            {showDateFilterDropdown && (
              <div className="gameInLeagueAuthPage-filterDropdown">
                <div className="gameInLeagueAuthPage-filterDropdownHeader">
                  <span>Dates</span>
                  <button type="button" onClick={clearDateFilters}>
                    Clear
                  </button>
                </div>

                <div className="gameInLeagueAuthPage-filterOptionList">
                  {uniqueDates.length > 0 ? (
                    uniqueDates.map((date) => (
                      <button
                        key={date}
                        type="button"
                        className={`gameInLeagueAuthPage-filterOption ${dateFilters.includes(date)
                            ? "gameInLeagueAuthPage-filterOptionActive"
                            : ""
                          }`}
                        onClick={() => toggleDateFilter(date)}
                      >
                        <span className="gameInLeagueAuthPage-filterCheckbox">
                          {dateFilters.includes(date) ? "✓" : ""}
                        </span>

                        <span className="gameInLeagueAuthPage-filterDateText">
                          {format(parseISO(date), "MMMM d, yyyy")}
                        </span>
                      </button>
                    ))
                  ) : (
                    <p className="gameInLeagueAuthPage-emptyFilterText">
                      No game dates yet.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {dateFilters.length > 0 && (
            <button
              type="button"
              className="gameInLeagueAuthPage-clearFilterBtn"
              onClick={clearDateFilters}
            >
              Clear Filters
            </button>
          )}
        </div>


      </div>

      {message && (
        <div className="gameInLeagueAuthPage-message">
          {message}
        </div>
      )}

      <div className="gameInLeagueAuthPage-tableCard">
        <GameScheduleTable
          message={message}
          games={games}
          byes={byes}
          displayRows={displayRows}
          dateFilters={dateFilters}
          selectedGames={selectedGames}
          setSelectedGames={setSelectedGames}
          selectedByes={selectedByes}
          setSelectedByes={setSelectedByes}
          selectedWeekOffs={selectedWeekOffs}
          setSelectedWeekOffs={setSelectedWeekOffs}
          getGameDate={getGameDate}
          getGameTime={getGameTime}
          getTeamNameById={getTeamNameById}
          scoreMismatches={scoreMismatches}
          navigate={navigate}
          openScoreModal={openScoreModal}
        />
      </div>

      {showWeekOffModal && (
        <CreateWeekOffModal
          weekOffForm={weekOffForm}
          setWeekOffForm={setWeekOffForm}
          handleCreateWeekOff={handleCreateWeekOff}
          setShowWeekOffModal={setShowWeekOffModal}
        />
      )}

      {isByeModalOpen && (
        <CreateByeModal
          leagueType={leagueType}
          byeForm={byeForm}
          setByeForm={setByeForm}
          teams={teams}
          players={leaguePlayers}
          handleCreateBye={handleCreateBye}
          setIsByeModalOpen={setIsByeModalOpen}
        />
      )}

      {isModalOpen && (
        <>
          <CreateGameModal
            leagueType={leagueType}
            teams={teams}
            players={leaguePlayers}
            existingGames={games}
            handleCreateGames={handleCreateGames}
            setIsModalOpen={setIsModalOpen}
            savedLocations={savedLocations}
            setShowCreateLocationModal={setShowCreateLocationModal}
            setShowEditLocationsModal={setShowEditLocationsModal}
            setNewSavedLocationForm={setNewSavedLocationForm}
          />

          <SavedLocationModals
            showCreateLocationModal={showCreateLocationModal}
            setShowCreateLocationModal={setShowCreateLocationModal}
            showEditLocationsModal={showEditLocationsModal}
            setShowEditLocationsModal={setShowEditLocationsModal}
            newSavedLocationForm={newSavedLocationForm}
            setNewSavedLocationForm={setNewSavedLocationForm}
            editingSavedLocations={editingSavedLocations}
            setEditingSavedLocations={setEditingSavedLocations}
            savedLocations={savedLocations}
            handleCreateSavedLocation={handleCreateSavedLocation}
            handleEditSavedLocationField={handleEditSavedLocationField}
            handleSaveEditedLocations={handleSaveEditedLocations}
            handleDeleteSavedLocation={handleDeleteSavedLocation}
          />
        </>
      )}

      {isScoreModalOpen && selectedGame && (
        <UpdateScoreModal
          selectedGame={selectedGame}
          scoreForm={scoreForm}
          handleScoreChange={handleScoreChange}
          handleUpdateScore={handleUpdateScore}
          setIsScoreModalOpen={setIsScoreModalOpen}
          getTeamNameById={getTeamNameById}
        />
      )}
    </div>
  );
}

export default LeagueGamesAuth;